/**
 * S8 — derive cross-filter `FilterBinding`s from a VQE-built query's resolved
 * structure. VQE knows which output column maps to which real table column
 * (`selectItems[].resolved`, populated ONLY for plain column refs — expressions
 * and function calls stay unbound by design), so VQE tiles get their S11
 * cross-filter bindings for free at save time.
 *
 * `targetExpression` is the column identifier — qualified `alias.column`
 * when more than one relation is in scope (S11 validates + quotes every
 * dot-part per engine — never raw SQL). `valueType` comes from the preview's
 * detected column categories; anything unknown degrades to 'string'.
 */
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { ColumnTypeInfo } from '../../../charts/types/chartTypes';
import type { FilterBinding } from '../../types/dashboardTypes';

/** Matches the S11 predicate composer's per-part identifier validation. */
const PLAIN_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_$]*$/;

const categoryToValueType = (
  category: ColumnTypeInfo['category'] | undefined,
): FilterBinding['valueType'] => {
  switch (category) {
    case 'numeric':
      return 'number';
    case 'date':
      return 'date';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
};

export function deriveVqeFilterBindings(
  structure: QueryStructure | null,
  columnInfos: ColumnTypeInfo[],
): FilterBinding[] {
  if (!structure) return [];
  // C3-R2 (Codex 5): with more than one relation in scope (joins), a bare
  // column target can be AMBIGUOUS (`o.region` vs `c.region`) — qualify with
  // the resolved relation's alias (or leaf table name). Single-relation
  // queries keep bare targets; a qualifier that isn't plain-identifier
  // shaped degrades to the bare column rather than failing validation later.
  const relationCount = (structure.fromItems?.length ?? 0) + (structure.joins?.length ?? 0);
  const seen = new Set<string>();
  const bindings: FilterBinding[] = [];
  for (const item of structure.selectItems ?? []) {
    if (item.resolved?.kind !== 'column') continue;
    const column = item.resolved.column.name;
    const dashboardField = item.alias ?? column;
    const key = dashboardField.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const info = columnInfos.find((c) => c.name.toLowerCase() === key);
    let targetExpression = column;
    if (relationCount > 1) {
      const relation = item.resolved.relation;
      const qualifier =
        relation?.via === 'alias' ? relation.aliasRef?.alias : relation?.qualified?.name;
      if (qualifier && PLAIN_IDENTIFIER.test(qualifier)) {
        targetExpression = `${qualifier}.${column}`;
      }
    }
    bindings.push({
      dashboardField,
      targetExpression,
      valueType: categoryToValueType(info?.category),
    });
  }
  return bindings;
}
